import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { type DailyClicks } from '@/domain/results/types';
import { formatCount, formatReportDay } from '@/domain/results/utils';
import { useTranslate } from '@/state/i18n';
import { controlTokens, radiusTokens } from '@/theme/themePrimitives';

interface Props {
  days: DailyClicks[];
}

/** Bars of clicks per day. A day under 20 people shows a dashed stub, without the number. */
const ClicksByDayChart = ({ days }: Props) => {
  const t = useTranslate('domain.results.components.ClicksByDayChart');
  const visible = days.filter((day) => day.clicks !== null);
  const max = Math.max(1, ...visible.map((day) => day.clicks ?? 0));
  const hasHidden = visible.length < days.length;

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Typography variant="subtitle2" fontWeight={700}>
        {t('title')}
      </Typography>
      {days.length ? (
        <>
          <Box sx={sx.bars} role="img" aria-label={t('aria')}>
            {days.map((day) => (
              <Tooltip
                key={day.day}
                title={`${formatReportDay(day.day)} · ${
                  day.clicks === null ? t('hidden') : t('clicks', { count: day.clicks })
                }`}
              >
                <Box
                  sx={[
                    sx.bar,
                    day.clicks === null
                      ? sx.hiddenBar
                      : { height: `${Math.max(4, (day.clicks / max) * 100)}%` },
                  ]}
                />
              </Tooltip>
            ))}
          </Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {formatReportDay(days[0].day)}
            </Typography>
            {visible.length ? (
              <Typography variant="caption" color="text.secondary">
                {t('max', { value: formatCount(max) })}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {formatReportDay(days[days.length - 1].day)}
            </Typography>
          </Stack>
          {hasHidden ? (
            <Typography variant="caption" color="text.secondary">
              {visible.length ? t('hiddenLegend') : t('allHidden')}
            </Typography>
          ) : null}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {t('empty')}
        </Typography>
      )}
    </Paper>
  );
};

export default ClicksByDayChart;

const sx = {
  paper: {
    borderRadius: radiusTokens.xxl,
    p: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },
  bars: { height: 160, display: 'flex', alignItems: 'flex-end', gap: '3px' },
  bar: {
    flex: 1,
    minWidth: 3,
    borderRadius: '4px 4px 0 0',
    bgcolor: controlTokens.cobalt,
  },
  hiddenBar: {
    height: '10%',
    bgcolor: 'transparent',
    border: '1px dashed',
    borderColor: 'divider',
  },
};
